import { useState, useEffect, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';

interface CollapsibleSectionProps {
  /** Unique section identifier within this page */
  id: string;
  /** localStorage namespace key: typically `${pageKey}:${userId}` */
  storageKey: string;
  title: string;
  children: ReactNode;
  /** Whether the section starts open when no persisted state exists (default: true) */
  defaultOpen?: boolean;
  /** When true, shows a drag handle and the section header is styled for design mode */
  isDragMode?: boolean;
  /** Drag-and-drop event handlers injected by PageSectionsLayout */
  dragProps?: {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onDragEnd: () => void;
  };
  /** Extra class on the outer wrapper */
  className?: string;
}

function storageId(storageKey: string, sectionId: string) {
  return `sections:${storageKey}:${sectionId}:open`;
}

export function CollapsibleSection({
  id,
  storageKey,
  title,
  children,
  defaultOpen = true,
  isDragMode = false,
  dragProps,
  className = '',
}: CollapsibleSectionProps) {
  const key = storageId(storageKey, id);

  const [isOpen, setIsOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? stored === 'true' : defaultOpen;
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, String(isOpen));
    } catch {
      // ignore storage errors
    }
  }, [isOpen, key]);

  const toggle = () => setIsOpen((v) => !v);

  return (
    <section
      className={`bg-white rounded-xl border transition-colors ${
        isDragMode
          ? 'border-primary-300 shadow-sm cursor-grab active:cursor-grabbing'
          : 'border-gray-200'
      } ${className}`}
      {...dragProps}
    >
      {/* Section header — always visible */}
      <div className={`flex items-center gap-2 p-4 ${isOpen ? 'border-b border-gray-100' : ''}`}>
        {isDragMode && (
          <GripVertical
            size={16}
            className="text-gray-400 shrink-0 cursor-grab"
            aria-hidden="true"
          />
        )}

        <button
          onClick={toggle}
          className="flex items-center gap-2 flex-1 text-left focus:outline-none group"
          aria-expanded={isOpen}
        >
          <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">
            {title}
          </span>
          <span className="ml-auto text-gray-400 group-hover:text-gray-600 transition-colors">
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        </button>
      </div>

      {/* Collapsible content */}
      {isOpen && <div className="p-5">{children}</div>}
    </section>
  );
}
